import React, { createContext, useState, useContext } from 'react';

const TestContext = createContext();

export const useTest = () => useContext(TestContext);

export const TestProvider = ({ children }) => {
  const [selectedArea, setSelectedArea] = useState(null);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: ''
  });
  
  // Reset all test data
  const resetTest = () => {
    setSelectedArea(null);
    setAnswers({});
    setResults(null);
    setUserInfo({
      name: '',
      email: ''
    });
  };
  
  // Update a single answer
  const updateAnswer = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };
  
  // Calculate test results based on answers
  const calculateResults = () => {
    const answersArray = Object.values(answers);
    
    if (answersArray.length === 0) return null;
    
    // Convert answer options to numerical values
    const valueMap = {
      'Casi nunca': 0,
      'A veces': 1,
      'A menudo': 2,
      'Siempre': 3
    };
    
    // Calculate total score
    const total = answersArray.reduce((sum, answer) => {
      return sum + (valueMap[answer] || 0);
    }, 0);
    
    // Determine stress level based on total score
    let level, description, color;
    
    // Get the number of questions that were actually answered to calculate max possible score
    const numQuestionsAnswered = answersArray.length;
    const maxPossibleScore = numQuestionsAnswered * 3; // Each question has max value 3
    const scorePercentage = (total / maxPossibleScore) * 100;
    
    if (scorePercentage < 20) {
      level = 'MUY LEVE';
      description = 'Tu nivel de malestar es muy bajo. Es importante mantener los buenos hábitos para continuar en esta línea.';
      color = '#4ecdc4'; // Verde-azulado para nivel muy leve
    } else if (scorePercentage < 40) {
      level = 'LEVE';
      description = 'Tu nivel de malestar es bajo. Aunque la situación es manejable, es recomendable adoptar estrategias preventivas.';
      color = '#97d5a0'; // Verde claro para nivel leve
    } else if (scorePercentage < 60) {
      level = 'MODERADO';
      description = 'Estás experimentando un nivel significativo de malestar que está comenzando a impactar diferentes áreas de tu vida. Este es un buen momento para implementar estrategias de manejo.';
      color = '#ff9800'; // Naranja para nivel moderado
    } else if (scorePercentage < 80) {
      level = 'GRAVE';
      description = 'Tu nivel de malestar es grave y está afectando significativamente tu calidad de vida. Es recomendable buscar apoyo profesional pronto.';
      color = '#ff7043'; // Naranja-rojo para nivel grave
    } else {
      level = 'MUY GRAVE';
      description = 'Estás experimentando un nivel muy elevado de malestar que probablemente está impactando seriamente tu funcionamiento diario. Es altamente recomendable buscar ayuda profesional lo antes posible.';
      color = '#ff5252'; // Rojo para nivel muy grave
    }
    
    const result = {
      level,
      description,
      color,
      scorePercentage,
      area: selectedArea
    };
    
    setResults(result);
    return result;
  };
  
  const value = {
    selectedArea,
    setSelectedArea,
    answers,
    updateAnswer,
    calculateResults,
    results,
    userInfo,
    setUserInfo,
    resetTest
  };
  
  return (
    <TestContext.Provider value={value}>
      {children}
    </TestContext.Provider>
  );
};
